<script setup lang="ts">
import GaiaText from '../../base/GaiaText.vue';
import { gaiaFontWeight, gaiaAccent, gaiaFontSize, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** Who set the value — renders the "Set by X" pill; omit for the bare circle. */
  by: {
    type: String,
    default: undefined,
  },
  /**
   * The 15px tinted square that rides at the end of a grid cell. The filled
   * circle carries too much weight at spreadsheet density, where a column can
   * show dozens of them at once.
   */
  tile: {
    type: Boolean,
    default: false,
  },
});

const accent = gaiaAccent.base;
const strong = gaiaAccent.strong;
const surface = gaiaAccent.surface;
const border = gaiaAccent.border;
const halo = gaiaAccent.halo;
const onAccent = gaiaNeutral.surface;
// The pill is a bordered inline box with its own state, so its type reaches
// it through v-bind rather than a GaiaText prop.
const pillSize = gaiaFontSize.meta;
const semibold = String(gaiaFontWeight.semibold);
</script>

<template>
  <!-- Human-overwrite marker from the File Detail mock: violet person circle
       (bare, on extraction rows) or the "Set by X" pill (detail modal). -->
  <n-flex
    v-if="props.by"
    inline
    align="center"
    :size="7"
    :wrap="false"
    class="gaia-overwrite-badge pill"
    :title="`Set by ${props.by}`"
  >
    <n-flex
      inline
      align="center"
      justify="center"
      :size="0"
      :wrap="false"
      class="circle"
      :style="{ width: '18px', height: '18px' }"
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 16 16"
        fill="none"
        :stroke="onAccent"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M8 8.4a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z" />
        <path d="M3.4 13.2c.6-1.9 2.4-3 4.6-3s4 1.1 4.6 3" />
      </svg>
    </n-flex>
    <gaia-text tone="accent">Set by {{ props.by }}</gaia-text>
  </n-flex>
  <n-flex
    v-else
    inline
    align="center"
    justify="center"
    :size="0"
    :wrap="false"
    class="gaia-overwrite-badge circle"
    title="Manually overwritten"
    :style="{ width: '20px', height: '20px', boxShadow: `0 0 0 2px ${halo}` }"
  >
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      :stroke="onAccent"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M8 8.4a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z" />
      <path d="M3.4 13.2c.6-1.9 2.4-3 4.6-3s4 1.1 4.6 3" />
    </svg>
  </n-flex>
</template>

<style scoped>
.circle {
  flex: none;
  border-radius: 50%;
  background: v-bind(accent);
}

.pill {
  height: 24px;
  padding: 0 10px 0 3px;
  border: 1px solid v-bind(border);
  border-radius: 12px;
  background: v-bind(surface);
  font-size: v-bind(pillSize);
  font-weight: v-bind(semibold);
  color: v-bind(strong);
}
</style>
